/**
 * BaselineRecommendationCard Component
 * 
 * Card component that displays current and recommended baseline values for ultrasound
 * measurements. Uses Meridian components for consistent styling and layout.
 */

import React from 'react';
import Card from '@amzn/meridian/card';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import { useUltsBaselineRecommendation } from '../../../hooks';

export interface BaselineRecommendationCardProps {
  /** Site identifier */
  site: string;
  /** Asset identifier */
  asset: string;
  /** Position identifier (may include equipment prefix) */
  position: string;
}

/**
 * BaselineRecommendationCard component that displays AI-powered baseline recommendations
 * for ultrasound measurements using Meridian components for layout and styling.
 */
export const BaselineRecommendationCard: React.FC<BaselineRecommendationCardProps> = ({
  site,
  asset,
  position
}) => {
  console.log(`BaselineRecommendationCard - site ${site} asset ${asset} position ${position}`);
  const { data, isLoading, error, retry } = useUltsBaselineRecommendation({
    site,
    asset,
    position
  });

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Baseline Recommendation</Text>
          <Row spacing="200">
            <Loader size="small" type="circular" />
            <Text type="b200">Loading recommendation...</Text>
          </Row>
        </Column>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Baseline Recommendation</Text>
          <Alert type="error">
            <Column spacing="100">
              <Text type="b200">{error}</Text>
              <Button type="secondary" size="small" onClick={retry}>
                Retry
              </Button>
            </Column>
          </Alert>
        </Column>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Baseline Recommendation</Text>
          <Alert type="neutral">
            <Text type="b200">No recommendation available for this position</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  // Data display state
  return (
    <Card>
      <Column spacing="300">
        <Text type="h100">Baseline Recommendation</Text>
        <Row spacing="100">
          <Text type="b300">Current:</Text>
          <Text type="b200">{data.currentBaseline} dB</Text>
        </Row>
        
        <Column spacing="200">
          <Row spacing="100">
            <Text type="b300">Recommended:</Text>
            <Text type="b200">{data.recommendation} dB</Text>
          </Row>
          
          <Column spacing="100">
            <Text type="b300">Reason: {data.reason}</Text>
            
          </Column>
        </Column>
      </Column>
    </Card>
  );
};